import { INSURABLE_GROUPS } from '@/features/quotes/data/group-life/insurable-groups';
import type { GroupLifeTarifacaoLife } from '@/services/group-life';
import { SearchFilterField } from '@/shared/ui';
import { formatCurrency, formatDateToBrazil } from '@/shared/utils';
import {
    Button,
    Pagination,
    Spinner,
    Text,
} from '@noorden-insurtech/alba-design-system';
import { ChevronRight } from 'lucide-react';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import {
    PME_TARIFACAO_PAGE_SIZE,
    type PmeTarifacaoContext,
    usePmeTarifacaoPage,
} from './use-pme-tarifacao.hook';

type Props = {
  policyId: string;
  context: PmeTarifacaoContext;
  /**
   * Bucket das vidas: `__STIPULATOR__` = estipulante master, id do sub = só as
   * vidas dele. Sem isso a grade lista a apólice inteira — e o card de cada
   * estipulante mostrava as vidas de todos.
   */
  targetId?: string;
};

/**
 * Grid de vidas com formato tarifação SIMPLIFICADO: mostra Capital e Prêmio
 * total (`duePremium`) — sem Prêmio puro, Prêmio líquido, Prêmio bruto e IOF
 * (a abertura completa fica na página de Tarifação). Os prêmios anuais da API
 * são convertidos para valores MENSAIS nesta grade. A linha da vida e o
 * "Total da vida" usam `totalDuePremium / 12`, então fecham com a soma da
 * coluna. Usado nos resumos da cotação e da proposta PME.
 */
export function SummaryLivesGrid({ policyId, context, targetId }: Props) {
  const [searchInput, setSearchInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PME_TARIFACAO_PAGE_SIZE);
  const search = useDebouncedValue(searchInput.trim(), 400);

  const {
    data,
    isLoading,
    isError,
    refetch,
    isPlaceholderData,
  } = usePmeTarifacaoPage(
    policyId,
    context,
    currentPage,
    search,
    pageSize,
    targetId
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [search, pageSize, targetId]);

  const lives = data?.data ?? [];
  const totalItems = data?.meta.total ?? 0;
  const totalPages = data?.meta.totalPages ?? 0;
  const hasSearch = search.length > 0;

  return (
    <section className='overflow-hidden rounded-2xl bg-white shadow-[0px_4px_24px_rgba(15,22,30,0.08)]'>
      <div className='flex flex-wrap items-center gap-3 border-b border-alba-gray-200 px-4 py-4 sm:px-6'>
        <Text className='font-montserrat text-base text-alba-black-500'>
          Lista de Vidas
        </Text>
        {totalItems > 0 && (
          <span className='rounded-full bg-alba-gray-100 px-3 py-0.5 font-montserrat text-xs font-bold text-alba-black-500'>
            {totalItems} {totalItems === 1 ? 'vida' : 'vidas'}
          </span>
        )}
        {isPlaceholderData && (
          <span className='flex items-center gap-2'>
            <Spinner size='sm' />
            <Text className='font-montserrat text-xs text-alba-gray-600'>
              Buscando…
            </Text>
          </span>
        )}
        <div className='ml-auto w-full sm:w-72'>
          <SearchFilterField
            value={searchInput}
            onChange={value => setSearchInput(String(value ?? ''))}
            placeholder='Buscar por nome ou CPF…'
          />
        </div>
      </div>

      {isError ? (
        <div className='flex flex-col items-center gap-3 px-6 py-10 text-center'>
          <Text className='font-montserrat text-sm text-alba-error-red-500'>
            Erro ao carregar a tarifação. Tente novamente.
          </Text>
          <Button
            variant='secondary'
            size='md'
            onClick={() => void refetch()}
          >
            Tentar novamente
          </Button>
        </div>
      ) : isLoading ? (
        <LivesSkeleton rows={6} withHeader />
      ) : lives.length === 0 ? (
        <div className='px-6 py-10 text-center'>
          <Text className='font-montserrat text-sm text-alba-gray-600'>
            {hasSearch
              ? 'Nenhuma vida encontrada para essa busca.'
              : 'Nenhuma vida encontrada.'}
          </Text>
        </div>
      ) : (
        <div
          className={`p-4 sm:p-6 ${
            isPlaceholderData
              ? 'pointer-events-none opacity-60 transition-opacity'
              : ''
          }`}
          aria-busy={isPlaceholderData}
        >
          {/*
            `overflow-x-auto` recorta TAMBÉM o eixo Y (o browser resolve o outro
            eixo como `auto`), o que comia a sombra do card da tabela. O padding
            dá o respiro para a sombra e a margem negativa devolve o espaçamento
            original do bloco.
          */}
          <div className='-my-2 overflow-x-auto py-2'>
            <div className='min-w-175 overflow-hidden rounded-3xl bg-white shadow-sm'>
              <LivesGridHeader />
              {lives.map(life => (
                <LifeRow key={life.policyElementId} life={life} />
              ))}
              {lives.length === 0 && (
                <div className='border-b border-alba-gray-300 px-4 py-6 text-center'>
                  <Text className='font-montserrat text-sm text-alba-gray-600'>
                    Nenhuma vida encontrada para essa busca.
                  </Text>
                </div>
              )}
            </div>
          </div>
          {totalPages > 0 && (
            <div className='flex justify-center pt-4'>
              <Pagination
                count={totalPages * pageSize}
                page={currentPage}
                pageSize={pageSize}
                totalItems={totalItems}
                pageSizeOptions={[5, 10, 20, 30, 40, 50]}
                onPageChange={page => setCurrentPage(page.page)}
                onPageSizeSelect={nextPageSize => setPageSize(nextPageSize)}
              />
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// ─── Internals ───────────────────────────────────────────────────────────────

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

const GRID_COLS =
  'grid grid-cols-[28px_2fr_1.3fr_0.9fr_1.3fr_1.1fr_1.2fr] items-center gap-3 px-4';

function LivesGridHeader() {
  const cell =
    'font-montserrat text-xs font-bold uppercase text-alba-black-500';
  return (
    <div
      className={`${GRID_COLS} border-b-2 border-alba-gray-300 bg-alba-gray-200 py-4`}
    >
      <span />
      <Text className={cell}>Nome</Text>
      <Text className={cell}>Grupo</Text>
      <Text className={cell}>Sexo</Text>
      <Text className={cell}>Nascimento</Text>
      <Text className={`${cell} text-right`}>Capital</Text>
      <Text className={`${cell} text-right`}>Prêmio total (Mensal)</Text>
    </div>
  );
}

function LifeRow({ life }: { life: GroupLifeTarifacaoLife }) {
  const [expanded, setExpanded] = useState(false);

  const genderLabel =
    life.gender === 'M' ? 'Masculino' : life.gender === 'F' ? 'Feminino' : '—';
  const groupLabel =
    INSURABLE_GROUPS.find(group => group.value === life.insurableGroup)
      ?.label ??
    life.insurableGroup ??
    '—';
  const birth = life.birthDate ? formatDateToBrazil(life.birthDate) : '—';
  const age = life.birthDate
    ? Math.floor(
        DateTime.now().diff(DateTime.fromISO(life.birthDate), 'years').years,
      )
    : null;

  return (
    <>
      <button
        type='button'
        onClick={() => setExpanded(previous => !previous)}
        aria-expanded={expanded}
        className={`${GRID_COLS} w-full cursor-pointer border-b border-alba-gray-300 bg-white py-4 text-left last:border-b-0 hover:bg-alba-gray-100/50`}
      >
        <ChevronRight
          className={`h-4 w-4 transition-transform ${
            expanded
              ? 'rotate-90 text-alba-success-green-500'
              : 'text-alba-gray-600'
          }`}
        />
        <Text className='truncate font-montserrat text-sm font-bold text-alba-black-500'>
          {life.name || '—'}
        </Text>
        <span className='w-fit rounded bg-[#EEEDF5] px-2 py-0.5 font-montserrat text-[10px] font-bold uppercase text-[#2C2864]'>
          {groupLabel}
        </span>
        <Text className='font-montserrat text-sm text-alba-gray-600'>
          {genderLabel}
        </Text>
        <Text className='font-montserrat text-sm text-alba-gray-600'>
          {birth}
          {age != null ? ` · ${age} anos` : ''}
        </Text>
        <Text className='text-right font-montserrat text-sm tabular-nums text-alba-gray-600'>
          {life.capital || '—'}
        </Text>
        <Text className='text-right font-montserrat text-sm font-bold tabular-nums text-alba-black-500'>
          {formatCurrency(life.totalDuePremium / 12)}
        </Text>
      </button>

      {expanded && (
        <div className='border-b border-alba-gray-300 bg-alba-gray-100/40 px-4 py-4'>
          {life.hasOwnCoverages ? (
            <CoveragesTable life={life} />
          ) : (
            <Text className='font-montserrat text-xs text-alba-gray-600'>
              Vida sem coberturas próprias — herda as coberturas do grupo
              segurável (sem override individual).
            </Text>
          )}
        </div>
      )}
    </>
  );
}

const COVERAGE_COLS =
  'grid grid-cols-[2.2fr_1.1fr_1fr] items-center gap-3 px-4';

function CoveragesTable({ life }: { life: GroupLifeTarifacaoLife }) {
  const header =
    'font-montserrat text-xs font-bold uppercase text-alba-black-500';
  const cell = 'font-montserrat text-xs tabular-nums text-alba-gray-600';
  const money = (value: number | null) =>
    value != null ? formatCurrency(value) : '—';
  const capital = (value: number | null) =>
    value != null && value !== 0 ? formatCurrency(value) : '—';

  return (
    <div className='ml-7'>
      <div className='mb-2 flex items-baseline gap-2'>
        <Text className='font-montserrat text-sm font-bold text-alba-black-500'>
          Coberturas
        </Text>
        <Text className='font-montserrat text-xs font-normal text-alba-gray-600'>
          (prêmios mensais)
        </Text>
      </div>
      <div className='overflow-hidden rounded-xl bg-white shadow-sm'>
        <div
          className={`${COVERAGE_COLS} border-b-2 border-alba-gray-300 bg-alba-gray-200 py-2.5`}
        >
          <Text className={header}>Cobertura</Text>
          <Text className={`${header} text-right`}>Capital</Text>
          <Text className={`${header} text-right`}>Prêmio total (Mensal)</Text>
        </div>
        {life.coverages.map(coverage => (
          <div
            key={`${coverage.code}-${coverage.name}`}
            className={`${COVERAGE_COLS} border-b border-alba-gray-300 py-2.5`}
          >
            <Text className='font-montserrat text-xs font-medium text-alba-black-500'>
              {coverage.name}
            </Text>
            <Text className={`${cell} text-right`}>
              {capital(coverage.sumInsured)}
            </Text>
            <Text className={`${cell} text-right`}>
              {money(
                coverage.duePremium != null
                  ? coverage.duePremium / 12
                  : null,
              )}
            </Text>
          </div>
        ))}
        <div className={`${COVERAGE_COLS} bg-alba-gray-200 py-2.5`}>
          <Text className='font-montserrat text-xs font-bold text-alba-black-500'>
            Total da vida
          </Text>
          <span />
          <Text className='text-right font-montserrat text-xs font-bold tabular-nums text-alba-black-500'>
            {formatCurrency(life.totalDuePremium / 12)}
          </Text>
        </div>
      </div>
    </div>
  );
}

function LivesSkeleton({
  rows,
  withHeader = false,
}: {
  rows: number;
  withHeader?: boolean;
}) {
  return (
    <div>
      {withHeader && (
        <div className='border-b-2 border-alba-gray-300 bg-alba-gray-200 px-4 py-4'>
          <div className='h-3 w-1/3 animate-pulse rounded bg-alba-gray-300' />
        </div>
      )}
      {Array.from({ length: rows }).map((_, index) => (
        <div
          key={index}
          className={`${GRID_COLS} border-b border-alba-gray-300 py-4 last:border-b-0`}
        >
          <span />
          {Array.from({ length: 6 }).map((_, cellIndex) => (
            <div
              key={cellIndex}
              className='h-3 animate-pulse rounded bg-alba-gray-200'
              style={{ width: `${55 + ((index + cellIndex) % 4) * 12}%` }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
